/**
 * SurfaceAlignmentGrid — basis-aligned overlay grid for object-mode surface
 * operators while they are drawing on a locked face plane.
 *
 * The mesh is oriented so its local +X aligns with `basis.tangent`, +Y with
 * `basis.bitangent`, +Z with `basis.normal`; combined with a
 * positionLocal-XY-driven shader, visible grid intersections sit at integer
 * multiples of `getDynamicSnapSpacing(gridLogDist)` in the basis frame.
 */
import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import type { AppState } from "../app/storeTypes.ts";
import type { FaceBasis, Vec3 } from "../modeling/faceBasis.ts";
import { basisGridUV, basisUV, uvToWorld } from "../modeling/surfaceBoxMath.ts";
import { GRID_EXTENT } from "../units.ts";
import { useDispose } from "./hooks/useDispose.ts";
import {
  setSurfaceAlignmentGridNormal,
  setSurfaceAlignmentGridOffset,
  surfaceAlignmentGridMaterial,
} from "./surfaceAlignmentGridMaterial.ts";
import {
  getSurfaceSelectionHighlightSnapshot,
  type SurfaceSelectionHighlightSurface,
  subscribeSurfaceSelectionHighlight,
} from "./surfaceSelectionHighlight.ts";

const _basisX = new THREE.Vector3();
const _basisY = new THREE.Vector3();
const _basisZ = new THREE.Vector3();
const _matrix = new THREE.Matrix4();

const SURFACE_GRID_MARGIN_RATIO = 0.12;
const SURFACE_GRID_MIN_MARGIN = 0.1;
const SURFACE_GRID_MAX_MARGIN = 1;

export type SurfaceAlignmentGridState = Pick<AppState, "surfaceTool">;

export interface SurfaceAlignmentGridSelection {
  basis: FaceBasis | null;
}

export interface SurfaceAlignmentGridFrame {
  center: Vec3;
  width: number;
  height: number;
}

/**
 * Decide whether the surface alignment grid should render and, if so, which
 * basis it should align with. The grid renders during face-pick + footprint
 * for Box/Cylinder, and during face-pick + drawing for Poly. Inset's `edit`
 * stage owns its own visual feedback (offset loop + inset ring) and does
 * not benefit from the grid.
 */
export function selectSurfaceAlignmentGridState(
  state: SurfaceAlignmentGridState,
): SurfaceAlignmentGridSelection {
  const tool = state.surfaceTool;
  if (!tool || !tool.draft) return { basis: null };
  if (tool.kind === "box" || tool.kind === "cylinder") {
    if (tool.stage === "select" || tool.stage === "footprint") {
      return { basis: tool.draft.basis };
    }
  } else if (tool.kind === "poly") {
    if (tool.stage === "select" || tool.stage === "drawing") {
      return { basis: tool.draft.basis };
    }
  }
  return { basis: null };
}

export function surfaceAlignmentGridFrameFromSurfaces(
  basis: FaceBasis,
  surfaces: readonly SurfaceSelectionHighlightSurface[],
): SurfaceAlignmentGridFrame {
  let minU = Number.POSITIVE_INFINITY;
  let maxU = Number.NEGATIVE_INFINITY;
  let minV = Number.POSITIVE_INFINITY;
  let maxV = Number.NEGATIVE_INFINITY;

  for (const surface of surfaces) {
    for (const face of surface.faces) {
      for (const vertex of face.vertices) {
        const uv = basisUV(basis, vertex);
        minU = Math.min(minU, uv.u);
        maxU = Math.max(maxU, uv.u);
        minV = Math.min(minV, uv.v);
        maxV = Math.max(maxV, uv.v);
      }
    }
  }

  if (
    !Number.isFinite(minU) ||
    !Number.isFinite(maxU) ||
    !Number.isFinite(minV) ||
    !Number.isFinite(maxV)
  ) {
    return {
      center: basis.origin,
      width: GRID_EXTENT,
      height: GRID_EXTENT,
    };
  }

  const spanU = Math.max(maxU - minU, 0);
  const spanV = Math.max(maxV - minV, 0);
  const margin =
    surfaces.length === 1
      ? 0
      : Math.min(
          SURFACE_GRID_MAX_MARGIN,
          Math.max(
            SURFACE_GRID_MIN_MARGIN,
            Math.max(spanU, spanV) * SURFACE_GRID_MARGIN_RATIO,
          ),
        );
  const centerU = (minU + maxU) / 2;
  const centerV = (minV + maxV) / 2;

  return {
    center: uvToWorld(basis, centerU, centerV),
    width: Math.max(spanU + margin * 2, SURFACE_GRID_MIN_MARGIN * 2),
    height: Math.max(spanV + margin * 2, SURFACE_GRID_MIN_MARGIN * 2),
  };
}

export function SurfaceAlignmentGrid() {
  const surfaceTool = useStore((s) => s.surfaceTool);
  const highlightSnapshot = useSyncExternalStore(
    subscribeSurfaceSelectionHighlight,
    getSurfaceSelectionHighlightSnapshot,
    getSurfaceSelectionHighlightSnapshot,
  );
  const draftSelection = useMemo(
    () => selectSurfaceAlignmentGridState({ surfaceTool }),
    [surfaceTool],
  );
  const { basis } = draftSelection;
  const surfaceClipSurface =
    highlightSnapshot.selected.length === 1
      ? highlightSnapshot.selected[0]!
      : null;
  const frame = useMemo(
    () =>
      basis
        ? surfaceAlignmentGridFrameFromSurfaces(
            basis,
            highlightSnapshot.selected,
          )
        : null,
    [basis, highlightSnapshot.selected],
  );
  const meshRef = useRef<THREE.Mesh>(null);

  const geometry = useMemo(() => {
    if (!basis || !frame) return null;
    if (surfaceClipSurface) {
      return buildSurfaceClippedGridGeometry(basis, surfaceClipSurface);
    }
    return new THREE.PlaneGeometry(frame.width, frame.height);
  }, [basis, frame, surfaceClipSurface]);
  useDispose(geometry);

  // Re-orient the mesh whenever the selected surface basis changes.
  useEffect(() => {
    if (!basis || !frame) return;
    const activeBasis: FaceBasis = basis;
    const mesh = meshRef.current;
    if (!mesh) return;
    const b = activeBasis;
    _basisX.set(b.tangent[0], b.tangent[1], b.tangent[2]);
    _basisY.set(b.bitangent[0], b.bitangent[1], b.bitangent[2]);
    _basisZ.set(b.normal[0], b.normal[1], b.normal[2]);
    _matrix.makeBasis(_basisX, _basisY, _basisZ);
    mesh.quaternion.setFromRotationMatrix(_matrix);
    const center = surfaceClipSurface ? b.origin : frame.center;
    mesh.position.set(center[0], center[1], center[2]);
    setSurfaceAlignmentGridNormal(b.normal);
    const offset = basisGridUV(b, center);
    setSurfaceAlignmentGridOffset(offset.u, offset.v);
  }, [basis, frame, surfaceClipSurface]);

  if (!basis || !geometry) return null;

  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      material={surfaceAlignmentGridMaterial}
      userData={{ excludeFromExport: true }}
      renderOrder={-1}
    />
  );
}

function buildSurfaceClippedGridGeometry(
  basis: FaceBasis,
  surface: SurfaceSelectionHighlightSurface,
): THREE.BufferGeometry | null {
  let triangleCount = 0;
  for (const face of surface.faces) {
    if (face.vertices.length >= 3) {
      triangleCount += face.vertices.length - 2;
    }
  }
  if (triangleCount === 0) return null;

  const positions = new Float32Array(triangleCount * 9);
  let w = 0;

  for (const face of surface.faces) {
    const vertices = face.vertices;
    if (vertices.length < 3) continue;
    const origin = basisUV(basis, vertices[0]!);
    for (let i = 1; i < vertices.length - 1; i += 1) {
      const a = basisUV(basis, vertices[i]!);
      const b = basisUV(basis, vertices[i + 1]!);
      positions[w++] = origin.u;
      positions[w++] = origin.v;
      positions[w++] = 0;
      positions[w++] = a.u;
      positions[w++] = a.v;
      positions[w++] = 0;
      positions[w++] = b.u;
      positions[w++] = b.v;
      positions[w++] = 0;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geometry;
}
